import React, { useState, useEffect, useRef } from 'react';
import { LogOut, Wand2, AudioWaveform, Mic2, Ear, HelpCircle } from 'lucide-react';
import GlobalAudioPlayer from './components/GlobalAudioPlayer';
import LoginPage from './components/LoginPage';
import { getAllVoices } from './config/voices';
import { useAuth } from './contexts/AuthContext';
import { signOutUser } from './config/firebase';
import './index.css';
import { initializeUserQuota, getQuotaStatus } from './utils/quotaManager';
import QuotaDisplay from './components/QuotaDisplay';
import { QUOTA_CONFIG } from './config/constants';
import logo from './assets/logo4.png';
import { updateUserRetention } from './utils/enhancedAnalytics';
import { BuyCreditsButton } from "./components/PricingModal";
import { PaymentStatusModal } from "./components/PaymentStatusModal";
import BestPracticesModal from './components/starterModal';

// Import new components
import Sidebar from './components/Sidebar';
import GenerateSpeech from './components/GenerateSpeech';
import VoiceChanger from './components/VoiceChanger';
import VoiceCloning from './components/VoiceCloning';
import SpeechDenoiser from './components/SpeechDenoiser';

const App = () => {
  const { user, isAuthenticated } = useAuth();
  const [activeTab, setActiveTab] = useState('generate');

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    // Refresh voices list when switching to generate speech or voice changer tab
    if (tab === 'generate' || tab === 'voiceChanger') {
      fetchVoices();

      // If switching to voice changer and Akhil voice is selected, reset selection
      if (tab === 'voiceChanger' && selectedVoice === 36) {
        // Will use first available voice as default in the VoiceChanger component
        setSelectedVoice(null);
      }
    }
  };
  const [voices, setVoices] = useState([]);
  const [selectedVoice, setSelectedVoice] = useState(260);
  const [quotaStatus, setQuotaStatus] = useState(null);
  const [currentAudio, setCurrentAudio] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const [showPaymentStatus, setShowPaymentStatus] = useState(false);
  const [paymentParams, setPaymentParams] = useState(null);
  const [showStarterModal, setShowStarterModal] = useState(false);
  const dropdownRef = useRef(null);
  const headerRef = useRef(null);

  // Monitor authentication state
  useEffect(() => {
  }, [user, isAuthenticated]);

  // Create function to fetch voices
  const fetchVoices = async () => {
    if (user?.email) {
      try {
        const allVoices = await getAllVoices(user.email);
        setVoices(allVoices);
        // If no voice is selected or the selected voice isn't in the list,
        // select the first voice from the list
        if (!selectedVoice || !allVoices.find(v => v.id === selectedVoice)) {
          setSelectedVoice(allVoices[0]?.id || 266);
        }
      } catch (error) {
        console.error("Error fetching voices:", error);
      }
    }
  };

  // Fetch all voices when user changes
  useEffect(() => {
    fetchVoices();
  }, [user]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target) && 
          !event.target.closest('[data-dropdown-trigger="true"]')) {
        setShowDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (user) {
      const initQuota = async () => {
        await initializeUserQuota(user.uid, user.email);
        const status = await getQuotaStatus(user.uid);
        setQuotaStatus(status);
      };
      initQuota();

      // Update user retention data
      updateUserRetention(user.email);
    }
  }, [user]);

  useEffect(() => {
    const parsePaymentParams = () => {
      const urlParams = new URLSearchParams(window.location.search);
      const paymentParams = {
        razorpay_payment_id: urlParams.get('razorpay_payment_id'),
        razorpay_payment_link_id: urlParams.get('razorpay_payment_link_id'),
        razorpay_payment_link_reference_id: urlParams.get('razorpay_payment_link_reference_id'),
        razorpay_payment_link_status: urlParams.get('razorpay_payment_link_status'),
        razorpay_signature: urlParams.get('razorpay_signature')
      };

      // Only log if we have payment params
      if (paymentParams.razorpay_payment_id) {
        setPaymentParams(paymentParams);
        setShowPaymentStatus(true);
      }
    };

    parsePaymentParams();
  }, []); // Run once on mount

  useEffect(() => {
    const isFirstVisit = !localStorage.getItem('hasVisitedBefore');
    if (isFirstVisit) {
      setShowStarterModal(true);
      localStorage.setItem('hasVisitedBefore', 'true');
    }
  }, []);

  if (!isAuthenticated) {
    return <LoginPage />;
  }

  const handleAudioGenerated = (audioData) => {
    setCurrentAudio(audioData);
    setIsPlaying(true);
  };

  const handlePreviewPlay = (voiceId, previewUrl, voiceName) => {
    if (currentAudio?.url === previewUrl) {
      setIsPlaying(!isPlaying);
    } else {
      setCurrentAudio({
        url: previewUrl,
        title: `Preview: ${voiceName}`,
        type: 'preview',
        id: voiceId
      });
      setIsPlaying(true);
    }
  };

  const handleClosePlayer = () => {
    if (currentAudio) {
      // First stop playing
      setIsPlaying(false);
      // Small timeout to ensure audio stops before cleanup
      setTimeout(() => {
        setCurrentAudio(null);
      }, 100);
    }
  };

  return (
    <div className="bg-[#181A1B] text-[#EAECEF] min-h-screen h-screen flex flex-col">
      {/* Mobile header - visible until lg breakpoint (1024px) */}
      <header ref={headerRef} className="lg:hidden w-full h-[4rem] border-b border-transparent mt-[0.5rem]">
        <div className="h-full flex justify-between items-center pr-4 pl-4">
          {/* Logo for mobile view */}
          <div className="flex items-center gap-[0.25rem]">
            <img
              src={logo}
              alt="Lucid Logo"
              className="h-[2rem] w-[2.5rem]"
            />
            <div>
              <h1 className="text-[1rem] font-bold text-[#EAECEF]">
                Lucid TTS v1.8
              </h1>
            </div>
          </div>

          {/* Mobile menu button - visible until lg breakpoint */}
          <div className="flex items-center gap-2">
            <button
              onClick={() => setShowDropdown(!showDropdown)}
              className="p-[0.5rem] rounded-lg bg-[#178A94] text-white"
              aria-label="Menu"
              data-dropdown-trigger="true"
            >
              {showDropdown ? (
                // X icon when menu is open
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none"
                  stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="18" y1="6" x2="6" y2="18"></line>
                  <line x1="6" y1="6" x2="18" y2="18"></line>
                </svg>
              ) : (
                // Hamburger icon when menu is closed
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none"
                  stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="3" y1="12" x2="21" y2="12"></line>
                  <line x1="3" y1="6" x2="21" y2="6"></line>
                  <line x1="3" y1="18" x2="21" y2="18"></line>
                </svg>
              )}
            </button>
          </div>
        </div>

        {/* Mobile dropdown menu - absolute positioning with calculated values */}
        {showDropdown && (
          <div
            ref={dropdownRef}
            className="lg:hidden absolute z-[500] bg-[#2C2F30] rounded-lg shadow-lg border border-[#3A3D3E] p-4"
            style={{
              top: `${headerRef.current ? headerRef.current.getBoundingClientRect().height + 4 : 0}px`,
              left: '1rem',
              right: '1rem',
              width: 'calc(100% - 2rem)'
            }}
          >
            <div className="flex items-center gap-2 mb-3 border-b border-[#3A3D3E] pb-3">
              <img
                src={user?.metadata.photoURL || `https://ui-avatars.com/api/?name=${user?.email?.charAt(0)}`}
                alt="Profile"
                className="w-10 h-10 rounded-full border border-[#3A3D3E]"
              />
              <span className="text-[#B0B4BB] text-base flex-grow">
                {user?.displayName || user?.email?.split('@')[0]}
              </span>
            </div>

            <div className="space-y-3">
              {/* Mobile navigation menu */}
              <div className="space-y-2">
                <button
                  onClick={() => {
                    handleTabChange('generate');
                    setShowDropdown(false);
                  }}
                  className={`w-full flex items-center gap-2 px-4 py-3 rounded-lg ${
                    activeTab === 'generate' ? 'bg-[#178A94] text-white' : 'bg-[#181A1B] text-[#B0B4BB]'
                  }`}
                >
                  Generate Speech
                </button>
                <button
                  onClick={() => {
                    setActiveTab('voiceChanger');
                    setShowDropdown(false);
                  }}
                  className={`w-full flex items-center gap-2 px-4 py-3 rounded-lg ${
                    activeTab === 'voiceChanger' ? 'bg-[#178A94] text-white' : 'bg-[#181A1B] text-[#B0B4BB]'
                  }`}
                >
                  <AudioWaveform size={16} />
                  <span className="ml-2">Voice Changer</span>
                </button>
                <button
                  onClick={() => {
                    handleTabChange('voiceCloning');
                    setShowDropdown(false);
                  }}
                  className={`w-full flex items-center gap-2 px-4 py-3 rounded-lg ${
                    activeTab === 'voiceCloning' ? 'bg-[#178A94] text-white' : 'bg-[#181A1B] text-[#B0B4BB]'
                  }`}
                >
                  Voice Cloning
                </button>
                <button
                  onClick={() => {
                    handleTabChange('speechDenoiser');
                    setShowDropdown(false);
                  }}
                  className={`w-full flex items-center gap-2 px-4 py-3 rounded-lg ${
                    activeTab === 'speechDenoiser' ? 'bg-[#178A94] text-white' : 'bg-[#181A1B] text-[#B0B4BB]'
                  }`}
                >
                  Speech Denoiser
                </button>
              </div>

              {/* Credits info with proper styling */}
              <div className="bg-[#181A1B] p-3 rounded-lg">
                <div className="mb-1 text-sm text-[#B0B4BB]">Credits Remaining</div>
                <div className="text-[#EAECEF] font-medium">{quotaStatus?.remainingChars?.toLocaleString() || 0} / {(quotaStatus?.totalCharsLimit || QUOTA_CONFIG.FREE_TIER_CHARS).toLocaleString()}</div>
                <div className="w-full bg-[#2C2F30] rounded-full h-2 mt-2">
                  <div
                    className="bg-[#178A94] h-2 rounded-full"
                    style={{
                      width: `${((quotaStatus?.remainingChars || 0) / (quotaStatus?.totalCharsLimit || QUOTA_CONFIG.FREE_TIER_CHARS)) * 100}%`
                    }}
                  ></div>
                </div>
              </div>

              {/* Buy Credits button */}
              <BuyCreditsButton />

              {/* Help button */}
              <a
                href="https://dreamsync.gitbook.io/lucidtts/getting-started/quickstart"
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => setShowDropdown(false)} // Close dropdown after clicking
                className="flex items-center justify-center gap-2 w-full py-3 px-4
                          bg-transparent text-[#B0B4BB] font-medium rounded-lg
                          border border-[#178A94] hover:text-[#EAECEF] transition-colors"
              >
                <HelpCircle size={16} className="text-[#178A94]" />
                Help
              </a>

              {/* Sign Out button */}
              <button
                onClick={() => {
                  signOutUser();
                  setShowDropdown(false);
                }}
                className="w-full flex items-center justify-center gap-2 py-3 px-4 bg-red-600 text-white font-medium rounded-lg"
              >
                <LogOut size={16} />
                Sign Out
              </button>
            </div>
          </div>
        )}
      </header>
      
      {/* Main content flex container with sidebar and content area */}
      <div className="flex flex-1 min-h-0 h-full">
        {/* Sidebar component for desktop */}
        <Sidebar
          activeTab={activeTab}
          onTabChange={handleTabChange}
          user={user}
          quotaStatus={quotaStatus}
          totalCharsLimit={QUOTA_CONFIG.FREE_TIER_CHARS}
        />
        
        {/* Content area */}
        <div className="flex-1 lg:ml-[240px] h-full overflow-hidden">
          <main className="p-[1rem] overflow-y-auto scrollbar-thin scrollbar-thumb-[#178A94] scrollbar-track-transparent hover:scrollbar-thumb-[#146E75] pb-[8rem] h-full">
            {activeTab === 'generate' && (
              <GenerateSpeech
                user={user}
                voices={voices}
                selectedVoice={selectedVoice}
                setSelectedVoice={setSelectedVoice}
                setQuotaStatus={setQuotaStatus}
                onAudioGenerated={handleAudioGenerated}
                key={voices.length} /* Force re-render when voices array changes */
              />
            )}
            {activeTab === 'voiceChanger' && (
              <VoiceChanger
                user={user}
                voices={voices}
                selectedVoice={selectedVoice}
                setSelectedVoice={setSelectedVoice}
                setQuotaStatus={setQuotaStatus}
                onAudioGenerated={handleAudioGenerated}
                onClose={handleClosePlayer}
              />
            )}
            {activeTab === 'voiceCloning' && (
              <VoiceCloning
                onVoiceAdded={() => {
                  // Refresh voice list when a new voice is added
                  fetchVoices();
                }}
                onVoiceDeleted={() => {
                  // Refresh voice list when a voice is deleted
                  fetchVoices();
                }}
              />
            )}
            {activeTab === 'speechDenoiser' && (
              <SpeechDenoiser
                user={user}
                setQuotaStatus={setQuotaStatus}
                onAudioGenerated={handleAudioGenerated}
                onClose={handleClosePlayer}
              />
            )}
          </main>

          {/* Audio player */}
          {currentAudio && (
            <GlobalAudioPlayer
              audioUrl={currentAudio.url}
              title={currentAudio.title}
              isPlaying={isPlaying}
              onPlay={() => setIsPlaying(true)}
              onPause={() => setIsPlaying(false)}
              onClose={handleClosePlayer}
              userId={user.email}
              voiceId={selectedVoice}
            />
          )}
        </div>
      </div>

      {/* Modals */}
      {showStarterModal && (
        <BestPracticesModal 
          isOpen={showStarterModal} 
          onClose={() => setShowStarterModal(false)}
          animationUrl="https://lucidservergenerated.blob.core.windows.net/assets/animation.mp4?sp=r&st=2025-04-04T05:12:47Z&se=2027-08-18T13:12:47Z&spr=https&sv=2024-11-04&sr=c&sig=6FdjfhwaA5XCzPhDUhp0E7xBdsCMvU5fA%2B1loPnCW2w%3D"
        />
      )}

      {showPaymentStatus && paymentParams && (
        <PaymentStatusModal 
          paymentParams={paymentParams}
          onClose={() => {
            setShowPaymentStatus(false);
            // Clear URL parameters
            window.history.replaceState({}, '', window.location.pathname);
          }}
        />
      )}
    </div>
  );
};

export default App;